<script setup lang="ts">
import { computed, onMounted, onBeforeUnmount, ref, watch } from 'vue'
import { useConfirm } from '@/composables/useConfirm'
import type { ConfirmOptions } from '@/composables/useConfirm'
import { useFocusTrap } from '@/composables/useFocusTrap'
import i18n from '@/i18n'

/* The global composer: shared primitives must render without the plugin. */
const { t } = i18n.global

const { state, resolve } = useConfirm()
const dialogRef = ref(null)
const titleId = 'lx-modal-title'
const descId = 'lx-modal-desc'

// Falls back to the defaults `confirm()` would have applied, so the template
// keeps one shape whether or not a dialog is currently open.
const opts = computed<Required<ConfirmOptions>>(
  () =>
    state.options ?? {
      title: '',
      message: '',
      confirmLabel: t('common.confirm'),
      cancelLabel: t('common.cancel'),
      danger: false,
      icon: null,
      bodyHTML: '',
    }
)
const iconClass = computed(
  () =>
    opts.value.icon ||
    (opts.value.danger ? 'fa-solid fa-triangle-exclamation' : 'fa-regular fa-circle-question')
)

useFocusTrap(dialogRef, () => state.open)

function onKey(e) {
  if (state.open && e.key === 'Escape') resolve(false)
}
onMounted(() => window.addEventListener('keydown', onKey))
onBeforeUnmount(() => window.removeEventListener('keydown', onKey))

/* Lock body scroll while open so the modal stays anchored. */
watch(
  () => state.open,
  (open) => {
    if (typeof document === 'undefined') return
    document.documentElement.style.overflow = open ? 'hidden' : ''
  }
)
</script>

<template>
  <div
    id="lx-modal-root"
    class="modal-backdrop"
    :class="{ 'is-open': state.open }"
    role="presentation"
    @click.self="resolve(false)"
  >
    <div
      ref="dialogRef"
      class="modal"
      :class="{ 'modal--danger': opts.danger }"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="titleId"
      :aria-describedby="opts.message ? descId : undefined"
      tabindex="-1"
    >
      <header class="modal__head">
        <span class="modal__icon"><i :class="iconClass"></i></span>
        <div>
          <div :id="titleId" class="modal__title">{{ opts.title }}</div>
          <div :id="descId" class="modal__sub" v-html="opts.message"></div>
        </div>
      </header>
      <div v-if="opts.bodyHTML" class="modal__body" v-html="opts.bodyHTML"></div>
      <footer class="modal__foot">
        <button class="btn btn--ghost" type="button" @click="resolve(false)">
          {{ opts.cancelLabel || t('common.cancel') }}
        </button>
        <button
          type="button"
          :class="opts.danger ? 'btn btn--danger-ghost' : 'btn btn--primary'"
          @click="resolve(true)"
        >
          {{ opts.confirmLabel || t('common.confirm') }}
        </button>
      </footer>
    </div>
  </div>
</template>
